import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { useTestMode } from './useTestMode.js';
import { useViewStatePreservation } from './useViewStatePreservation.js';
import apiService from '../apiService';

export function useSessionView() {
  const sessions = ref([]);
  const loading = ref(true);
  const currentView = ref('sessionView'); // 'sessionView' or 'tierListView'
  const selectedAlbum = ref(null);
  
  // Infinite scroll state
  const hasNext = ref(true);
  const currentPage = ref(1);
  const loadingMore = ref(false);
  const error = ref(null);
  
  // Search state
  const searchQuery = ref('');
  const searchMode = ref('auto');
  const isSearchActive = ref(false);
  const searchResults = ref([]);
  
  // Configuration
  const limit = 5;
  const scrollThreshold = 200;
  
  // Intersection Observer
  let observer = null;
  
  // Test mode functionality
  const { showTestAlbums } = useTestMode();

  // View state preservation
  const { restoreViewState, captureViewState } = useViewStatePreservation();

  /**
   * Fetch initial sessions (first page)
   */
  const fetchInitialSessions = async (search = '', mode = 'auto') => {
    const startTime = performance.now();
    loading.value = true;
    error.value = null;
    
    try {
      const response = await apiService.fetchSessions(1, limit, search, mode);
      
      // Handle search vs normal mode
      if (search && search.trim()) {
        searchResults.value = response.data || [];
        isSearchActive.value = true;
        searchQuery.value = search;
        searchMode.value = mode;
      } else {
        sessions.value = response.data || [];
        isSearchActive.value = false;
        searchQuery.value = '';
        searchResults.value = [];
      }
      
      hasNext.value = response.hasNext;
      currentPage.value = 1;
      
      const loadTime = performance.now() - startTime;
      const resultCount = isSearchActive.value ? searchResults.value.length : sessions.value.length;
      console.log('Sessions initial load performance:', {
        operation: 'fetchInitialSessions',
        loadTime: `${Math.round(loadTime)}ms`,
        sessionCount: resultCount,
        isSearch: isSearchActive.value,
        searchTerm: searchQuery.value,
        target: '<2000ms'
      });
      
      // Set up infinite scroll listener after initial load
      if (currentView.value === 'sessionView') {
        await nextTick();
        setupScrollListener();
      }
      
    } catch (err) {
      error.value = err.message || 'Failed to load sessions';
      console.error('Error fetching initial sessions:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Load more sessions (pagination)
   */
  const loadMoreSessions = async () => {
    if (loadingMore.value || !hasNext.value) return;
    
    const startTime = performance.now();
    loadingMore.value = true;
    error.value = null;
    
    try {
      const nextPage = currentPage.value + 1;
      const response = await apiService.fetchSessions(nextPage, limit, searchQuery.value, searchMode.value);
      
      // Append new sessions to existing list (search or normal results)
      if (isSearchActive.value) {
        searchResults.value = [...searchResults.value, ...response.data];
      } else {
        sessions.value = [...sessions.value, ...response.data];
      }
      hasNext.value = response.hasNext;
      currentPage.value = nextPage;
      
      const loadTime = performance.now() - startTime;
      const totalCount = isSearchActive.value ? searchResults.value.length : sessions.value.length;
      console.log('Sessions pagination load performance:', {
        operation: 'loadMoreSessions',
        loadTime: `${Math.round(loadTime)}ms`,
        page: nextPage,
        sessionCount: response.data.length,
        totalSessions: totalCount,
        isSearch: isSearchActive.value,
        searchTerm: searchQuery.value,
        target: '<1000ms'
      });
      
      // Only re-setup scroll listener if there are more sessions to load
      if (response.hasNext) {
        await nextTick();
        // Add delay to prevent immediate re-trigger
        setTimeout(() => {
          setupScrollListener();
        }, 500);
      }
      
    } catch (err) {
      error.value = err.message || 'Failed to load more sessions';
      console.error('Error loading more sessions:', err);
    } finally {
      loadingMore.value = false;
    }
  };

  // Store scroll handler reference for cleanup
  let scrollHandler = null;

  /**
   * Setup scroll event listener for infinite scroll
   */
  const setupScrollListener = () => {
    // Clean up existing listener
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    
    // Don't set up scroll listener if no more sessions to load or not in session view
    if (!hasNext.value || currentView.value !== 'sessionView') {
      return;
    }
    
    // Use scroll event instead of intersection observer
    const handleScroll = () => {
      // Check if user scrolled near bottom
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Trigger when within 200px of bottom
      const isNearBottom = scrollTop + windowHeight >= documentHeight - scrollThreshold;
      
      if (isNearBottom && !loadingMore.value && hasNext.value) {
        console.log('Scroll triggered load more at position:', {
          scrollTop,
          windowHeight,
          documentHeight,
          threshold: documentHeight - scrollThreshold
        });
        
        // Remove listener to prevent multiple triggers during loading
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
        
        loadMoreSessions();
      }
    };
    
    // Debounce scroll events
    let scrollTimeout = null;
    const debouncedHandleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(handleScroll, 100);
    };
    
    scrollHandler = debouncedHandleScroll;
    window.addEventListener('scroll', scrollHandler, { passive: true });
    console.log('Scroll listener set up for infinite scroll');
  };

  /**
   * Clean up scroll listeners and observers
   */
  const cleanupScrollListener = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    
    // Remove scroll listener
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
  };

  // Fallback to old API for non-session view (tierlist)
  const fetchSessions = async () => {
    loading.value = true;
    try {
      const response = await apiService.WorkspaceSessions();
      // Handle new serverless API response structure: {success: true, data: Array}
      if (response && response.success && Array.isArray(response.data)) {
        // Sort sessions by session_date (newest first)
        sessions.value = response.data.sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
      } else if (Array.isArray(response)) {
        // Fallback for direct array response (legacy compatibility)
        sessions.value = response.sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
      } else {
        sessions.value = [];
        console.error('Sessions response structure invalid:', response);
      }
    } catch (error) {
      console.error('Error fetching sessions', error);
      sessions.value = [];
    } finally {
      loading.value = false;
    }
  };

  function toggleView() {
    currentView.value = currentView.value === 'sessionView' ? 'tierListView' : 'sessionView';
  }

  function handleAlbumClicked(album) {
    // Capture state before opening album detail modal
    captureViewState({
      viewMode: 'sessionView',
      filters: {
        sessionSearch: searchQuery.value || null,
        searchMode: searchMode.value || null
      }
    });

    selectedAlbum.value = album;
  }

  /**
   * Perform search across all sessions
   */
  const performSearch = async (query, mode = 'auto') => {
    if (!query.trim()) {
      clearSearch();
      return;
    }
    
    await fetchInitialSessions(query.trim(), mode);
  };

  /**
   * Clear search and return to normal session view
   */
  const clearSearch = async () => {
    searchQuery.value = '';
    searchMode.value = 'auto';
    isSearchActive.value = false;
    searchResults.value = [];
    
    // Reload normal sessions
    await fetchInitialSessions();
  };

  // Fetch sessions on mount if default view is sessionView
  onMounted(async () => {
    if (currentView.value === 'sessionView') {
      // Restore view state first (if applicable)
      const savedState = restoreViewState();
      if (savedState && savedState.viewMode === 'sessionView') {
        console.log('[SessionView] Restoring session state');

        // Restore search if present
        if (savedState.filters.sessionSearch) {
          await fetchInitialSessions(savedState.filters.sessionSearch, savedState.filters.searchMode || 'auto');
        } else {
          await fetchInitialSessions();
        }

        // Restore scroll position after DOM update
        await nextTick();
        window.scrollTo({
          top: savedState.scrollY,
          behavior: 'smooth'
        });

        console.log('[SessionView] State restored');
      } else {
        await fetchInitialSessions();
      }
    }
  });

  // Watch for view changes and fetch sessions when switching to sessionView
  watch(currentView, (newView) => {
    cleanupScrollListener(); // Clean up scroll listener when switching views
    if (newView === 'sessionView') {
      fetchInitialSessions();
    } else if (newView === 'tierListView') {
      fetchSessions(); // Use old method for tierlist view
    }
  });
  
  // Watch for test mode changes and reload data
  watch(showTestAlbums, () => {
    cleanupScrollListener();
    if (currentView.value === 'sessionView') {
      fetchInitialSessions();
    } else if (currentView.value === 'tierListView') {
      fetchSessions();
    }
  });

  // --- Now Listening Block Logic ---
  const latestSession = computed(() => {
    const sessionsToCheck = isSearchActive.value ? searchResults.value : sessions.value;
    if (!sessionsToCheck.length) return null;
    // sessions are already sorted by date desc
    return sessionsToCheck[0];
  });

  const nowListeningVisible = computed(() => {
    const session = latestSession.value;
    if (!session || !Array.isArray(session.albums)) return false;
    return session.albums.some(album => album.tier === 'Now Listening To');
  });

  // Only show albums with 'Now Listening To' tier in the block
  const nowListeningAlbums = computed(() => {
    const session = latestSession.value;
    if (!session || !Array.isArray(session.albums)) return [];
    return session.albums.filter(album => album.tier === 'Now Listening To');
  });

  // Filter out 'Now Listening To' albums from the latest session's album list for the regular session list
  const filteredSessions = computed(() => {
    const sessionsToFilter = isSearchActive.value ? searchResults.value : sessions.value;
    if (!sessionsToFilter.length) return [];
    // Deep clone sessions to avoid mutating original data
    const cloned = sessionsToFilter.map(session => ({
      ...session,
      albums: Array.isArray(session.albums) ? [...session.albums] : []
    }));
    if (nowListeningVisible.value && cloned[0]) {
      cloned[0].albums = cloned[0].albums.filter(album => album.tier !== 'Now Listening To');
    }
    return cloned;
  });

  const nowListeningSession = computed(() => {
    // Only return a session object if there are now listening albums
    if (nowListeningVisible.value && nowListeningAlbums.value.length > 0) {
      return {
        ...latestSession.value,
        albums: nowListeningAlbums.value
      };
    }
    return null;
  });

  return {
    sessions,
    loading,
    currentView,
    toggleView,
    selectedAlbum,
    handleAlbumClicked,
    // Now Listening block
    latestSession,
    nowListeningVisible,
    nowListeningSession,
    filteredSessions,
    // Infinite scroll state
    hasNext,
    loadingMore,
    error,
    // Infinite scroll actions
    loadMoreSessions,
    cleanupScrollListener,
    // Debug/manual trigger
    setupScrollListener,
    // Search functionality
    searchQuery,
    searchMode,
    isSearchActive,
    searchResults,
    performSearch,
    clearSearch
  };
} 